import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

declare global {
  interface Window {
    kakao: any;
  }
}

interface LocationSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectLocation: (address: string, placeName?: string, lat?: number, lng?: number) => boolean;
  title: string;
}

const LocationSearchModal = ({
  isOpen,
  onClose,
  onSelectLocation,
  title,
}: LocationSearchModalProps) => {
  const { t } = useTranslation();
  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<any>(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedPlace, setSelectedPlace] = useState<any>(null);
  const markers = useRef<any[]>([]);
  const [exclusionZones, setExclusionZones] = useState<string[]>([]);

  // 본점 주소
  const mainBranchAddress = '부산 부산진구 전포동 674-4';

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Fetch delivery exclusion zones from backend
  useEffect(() => {
    const fetchExclusionZones = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
        const response = await fetch(`${apiUrl}/settings/public/delivery-exclusion-zones`);
        const data = await response.json();
        setExclusionZones(data.zones || []);
      } catch (error) {
        console.error('Failed to fetch delivery exclusion zones:', error);
        // 기본값 설정
        setExclusionZones(['부산 기장군', '부산 강서구', '경남']);
      }
    };

    fetchExclusionZones();
  }, []);

  // Check if delivery is available
  const isDeliveryAvailable = (address: string): boolean => {
    // 부산 지역인지 먼저 확인
    if (!address.startsWith('부산')) {
      return false;
    }

    // 제외 지역 목록과 비교
    for (const zone of exclusionZones) {
      if (address.startsWith(zone)) {
        return false;
      }
    }

    return true;
  };

  // Initialize map
  useEffect(() => {
    if (!isOpen || !mapRef.current || !window.kakao) return;

    const kakao = window.kakao;

    // Create map centered on Busan Seomyeon
    const container = mapRef.current;
    const options = {
      center: new kakao.maps.LatLng(35.1557, 129.0597),
      level: 3,
    };

    const mapInstance = new kakao.maps.Map(container, options);
    setMap(mapInstance);

    // Add click event to map
    kakao.maps.event.addListener(mapInstance, 'click', function (mouseEvent: any) {
      const latlng = mouseEvent.latLng;

      // Get address from coordinates
      const geocoder = new kakao.maps.services.Geocoder();
      geocoder.coord2Address(latlng.getLng(), latlng.getLat(), (result: any, status: any) => {
        if (status === kakao.maps.services.Status.OK) {
          const address = result[0].address.address_name;
          setSelectedPlace({
            place_name: t('checkout.selectedLocation'),
            address_name: address,
            y: latlng.getLat(),
            x: latlng.getLng(),
          });

          // Clear existing markers
          markers.current.forEach(marker => marker.setMap(null));
          markers.current = [];

          // Add marker at clicked position
          const marker = new kakao.maps.Marker({
            position: latlng,
            map: mapInstance,
          });
          markers.current.push(marker);
        }
      });
    });
  }, [isOpen]);

  // Search places
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation(); // 부모 폼으로 이벤트 전파 방지

    if (!map || !searchKeyword.trim()) return;

    const kakao = window.kakao;
    const ps = new kakao.maps.services.Places();

    // Search with keyword
    ps.keywordSearch(searchKeyword, (data: any, status: any) => {
      if (status === kakao.maps.services.Status.OK) {
        setSearchResults(data);

        // Clear existing markers
        markers.current.forEach(marker => marker.setMap(null));
        markers.current = [];

        // Display markers for search results
        const bounds = new kakao.maps.LatLngBounds();

        data.forEach((place: any, index: number) => {
          const position = new kakao.maps.LatLng(place.y, place.x);
          const marker = new kakao.maps.Marker({
            position,
            map,
          });

          markers.current.push(marker);
          bounds.extend(position);

          // Add click event to marker
          kakao.maps.event.addListener(marker, 'click', () => {
            handleSelectPlace(place);
          });
        });

        // Fit map to show all markers
        map.setBounds(bounds);
      } else if (status === kakao.maps.services.Status.ZERO_RESULT) {
        alert(t('checkout.noSearchResults'));
        setSearchResults([]);
      }
    });
  };

  // Select a place from search results
  const handleSelectPlace = (place: any) => {
    setSelectedPlace(place);

    // Move map to selected place
    const kakao = window.kakao;
    const position = new kakao.maps.LatLng(place.y, place.x);
    map.setCenter(position);

    // Clear existing markers
    markers.current.forEach(marker => marker.setMap(null));
    markers.current = [];

    // Add marker at selected place
    const marker = new kakao.maps.Marker({
      position,
      map,
    });
    markers.current.push(marker);
  };

  // Confirm selection
  const handleConfirm = () => {
    if (selectedPlace) {
      const shouldClose = onSelectLocation(
        selectedPlace.address_name,
        selectedPlace.place_name,
        parseFloat(selectedPlace.y),
        parseFloat(selectedPlace.x)
      );

      // onSelectLocation이 true를 반환할 때만 모달을 닫음
      if (shouldClose) {
        onClose();
        // Reset state
        setSearchKeyword('');
        setSearchResults([]);
        setSelectedPlace(null);
      }
    } else {
      alert(t('checkout.selectLocationRequired'));
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden flex flex-col p-6">
            {/* Search Bar */}
            <form onSubmit={handleSearch} className="mb-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.stopPropagation(); // 엔터 키 이벤트 전파 방지
                    }
                  }}
                  placeholder={t('checkout.locationSearchPlaceholder')}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <button
                  type="submit"
                  onClick={(e) => e.stopPropagation()} // 클릭 이벤트 전파 방지
                  className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
                >
                  {t('checkout.search')}
                </button>
              </div>
            </form>

            {/* 빠른 선택 */}
            <div className="mb-4">
              <div className="text-sm font-semibold text-gray-700 mb-2">{t('checkout.quickSelect')}</div>
              <button
                type="button"
                onClick={() => {
                  if (!map) return;

                  const kakao = window.kakao;
                  const geocoder = new kakao.maps.services.Geocoder();

                  // 주소로 좌표 검색
                  geocoder.addressSearch(mainBranchAddress, (result: any, status: any) => {
                    if (status === kakao.maps.services.Status.OK) {
                      const place = {
                        place_name: t('checkout.mainBranchName'),
                        address_name: mainBranchAddress,
                        y: result[0].y,
                        x: result[0].x,
                      };
                      handleSelectPlace(place);
                    } else {
                      alert(t('checkout.mainBranchNotFound'));
                    }
                  });
                }}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg transition-colors text-sm font-medium"
              >
                {t('checkout.mainBranchQuick')}
              </button>
            </div>

            <div className="flex-1 flex flex-col md:flex-row gap-4 min-h-0">
              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="w-full md:w-80 border rounded-lg overflow-y-auto max-h-48 md:max-h-none">
                  <div className="p-3 bg-gray-50 border-b font-semibold">
                    {t('checkout.searchResults')}
                  </div>
                  <div className="divide-y">
                    {searchResults.map((place, index) => (
                      <div
                        key={index}
                        onClick={() => handleSelectPlace(place)}
                        className={`p-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                          selectedPlace?.id === place.id ? 'bg-primary-50' : ''
                        }`}
                      >
                        <div className="font-semibold text-gray-900">{place.place_name}</div>
                        <div className="text-sm text-gray-600 mt-1">{place.address_name}</div>
                        {place.road_address_name && (
                          <div className="text-sm text-gray-500 mt-1">
                            ({t('checkout.roadAddress')}) {place.road_address_name}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Map */}
              <div className="w-full h-[400px] md:flex-1 md:h-auto border rounded-lg overflow-hidden">
                <div ref={mapRef} className="w-full h-full min-h-[400px]" />
              </div>
            </div>

            {/* Selected Place Info */}
            {selectedPlace && (
              <div className="mt-4 p-4 bg-primary-50 border border-primary-200 rounded-lg">
                <div className="font-semibold text-gray-900">{t('checkout.selectedLocationLabel')}</div>
                <div className="text-sm text-gray-700 mt-1">
                  {selectedPlace.place_name !== t('checkout.selectedLocation') && (
                    <div>{selectedPlace.place_name}</div>
                  )}
                  <div>{selectedPlace.address_name}</div>
                </div>
                <div className={`mt-3 text-sm font-medium ${isDeliveryAvailable(selectedPlace.address_name) ? 'text-green-600' : 'text-orange-600'}`}>
                  {isDeliveryAvailable(selectedPlace.address_name) ? (
                    <div>
                      <div>{t('checkout.deliveryAvailable')}</div>
                      <div className="text-xs text-gray-600 mt-1">
                        {t('checkout.deliveryAvailableTime')}<br/>
                        {t('checkout.branchVisitTime')}
                      </div>
                    </div>
                  ) : (
                    <span>{t('checkout.deliveryUnavailable')}</span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-6 border-t">
            <button
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleConfirm}
              disabled={selectedPlace && !isDeliveryAvailable(selectedPlace.address_name)}
              className={`px-6 py-2 rounded-lg transition-colors ${
                selectedPlace && !isDeliveryAvailable(selectedPlace.address_name)
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-primary-600 hover:bg-primary-700 text-white'
              }`}
            >
              {t('checkout.selectComplete')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default LocationSearchModal;
